import L from "leaflet";
import _ from "lodash";
import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { MEMOIZED_DOMAIN_URL } from "../../util/HostUtils";
import MissingImageComponent from "./MissingImageComponent";
import RailInspectTileInfo from "./RailInspectTileInfo";
import { getDirectionLetter, getSecondaryArrayIndex } from "./RailInspectUtils";

const baseURL = `https://inspection${MEMOIZED_DOMAIN_URL}/`;

const DEBUG = false;

export const DynamicLayer = L.GridLayer.extend({
    railImages: null,
    index: null,
    arrayOffset: 0,
    brightness: 1,
    contrast: 1,
    deviceName: "",
    srcWidth: 0,
    imgWidth: 0,
    srcX: 0,
    xOffset: 0,
    yOffset: 0,
    reverse: false,
    horizontalFlip: false,
    secondary: false,
    constantArrayOffset: 0,
    zoom: 0,
    secondaryAlignment: { inter: 0, intra: 0 },
    horizontal: false,
    totalWidth: 0,
    baseXOffset: 0,
    backwardsImage: false,
    flipped: false,
    classifications: [],
    leftRightConfiguration: [],
    displayContamination: false,
    contaminationWidth: 0,
    targetInspectionData: null,
    sperryMarkers: [],
    primaryImages: [],
    csrfToken: null,
    isExporting: false,
    exportRange: null,
    sperryMarkersVisibility: true,
    primaryIsReversed: false,
    manualOffset: 0,
    setup: function (
        railImages,
        horizontal,
        classifications,
        displayContamination,
        targetInspectionData = null,
        sperryMarkers = [],
        debug = false,
        setSomeImagesMissing = null,
        sperryMarkersVisibility = true,
    ) {
        this.railImages = railImages;
        this.horizontal = horizontal;
        this.classifications = classifications;
        this.displayContamination = displayContamination;
        this.debug = debug;
        this.targetInspectionData = targetInspectionData;
        this.sperryMarkers = sperryMarkers;
        this.sperryMarkersVisibility = sperryMarkersVisibility;
        if (setSomeImagesMissing) {
            this.setSomeImagesMissing = setSomeImagesMissing;
        }
    },
    setIndex: function (index) {
        this.index = index;
    },
    setPrimaryIsReversed: function (primaryReversed) {
        this.primaryIsReversed = primaryReversed;
    },
    setCsrfToken: function (token) {
        this.csrfToken = token;
    },
    setArrayOffset: function (offset) {
        this.arrayOffset = offset;
    },
    setReverse: function (reverse) {
        this.reverse = reverse;
    },
    setFlipped: function (flipped) {
        this.flipped = flipped;
    },
    setBackwardImage: function (back) {
        this.backwardsImage = back;
    },
    setSecondary: function (secondary) {
        this.secondary = secondary;
    },
    setConstantOffset: function (offset) {
        if (!this.horizontal || !this.flipped) {
            offset *= -1;
        }
        if (this.horizontal && this.reverse) {
            this.constantArrayOffset = Math.ceil(offset);
            this.yOffset = (this.constantArrayOffset - offset) * 1000;
        } else if (this.horizontal) {
            this.constantArrayOffset = Math.floor(offset);
            this.yOffset = (this.constantArrayOffset - offset) * 1000;
        } else {
            this.constantArrayOffset = Math.floor(offset);
            this.yOffset = (offset - this.constantArrayOffset) * 1000;
        }
    },
    setSecondaryAlignment: function (alignment) {
        this.secondaryAlignment = alignment;
    },
    setHorizontalFlip: function (flip) {
        this.horizontalFlip = flip;
    },
    setBrightness: function (val) {
        this.brightness = Math.pow(3, val);
    },
    setContrast: function (val) {
        this.contrast = Math.pow(2, val);
    },
    setDeviceName: function (val) {
        this.deviceName = val;
    },
    setSrcWidth: function (val) {
        this.srcWidth = val;
    },
    setImgWidth: function (val) {
        this.imgWidth = val;
    },
    setDisplayWidth: function (val) {
        this.displayWidth = val;
    },
    setSrcX: function (val) {
        this.srcX = val;
    },
    setBaseXOffset: function (val) {
        this.baseXOffset = val;
    },
    setXOffset: function (val) {
        this.xOffset = val;
    },
    setTotalWidth: function (val) {
        this.totalWidth = val;
    },
    setLeftRightConfig: function (val) {
        this.leftRightConfiguration = val;
    },
    setMapZoom: function (zoom) {
        this.zoom = zoom;
    },
    setIsExporting: function (val) {
        this.isExporting = val;
    },
    setExportRange: function (val) {
        this.exportRange = val;
    },
    setContaminationWidth: function (val) {
        this.contaminationWidth = val;
    },
    setManualOffset: function (manualOffset) {
        this.manualOffset = manualOffset;
    },
    setSperryMarkerVisibility: function (val) {
        this.sperryMarkersVisibility = val;
    },
    createTile: function (coords, done) {
        let coordToCompare = this.horizontal ? coords.y : coords.x;
        let arrayIndexCoord = this.horizontal ? -coords.x : coords.y;

        let arrayIndex;
        let xOffset = this.xOffset;
        const xIndex = Math.floor((xOffset + this.baseXOffset) / this.options.tileSize.x);

        if (this.secondary && coordToCompare === xIndex) {
            arrayIndex = getSecondaryArrayIndex(
                arrayIndexCoord,
                this.secondaryAlignment,
                this.arrayOffset,
                this.primaryIsReversed,
                this.reverse,
                this.constantArrayOffset,
                this.manualOffset,
                "tile layer",
            );
        } else {
            arrayIndex = (this.reverse ? 1 : -1) * arrayIndexCoord + this.arrayOffset + this.constantArrayOffset;
        }

        let container = document.createElement("div");

        if (coordToCompare === xIndex && this.railImages && arrayIndex >= 0 && arrayIndex < this.railImages.length) {
            const imageTimestamp = this.railImages[arrayIndex].timestamp;

            let previousImageTimestamp = arrayIndex > 0 ? this.railImages[arrayIndex - 1].timestamp : -1;
            let nextImageTimestamp = arrayIndex < this.railImages.length - 1 ? this.railImages[arrayIndex + 1].timestamp : -1;

            setTimeout(() => {
                let tile = L.DomUtil.create("div");

                let base_path = this.railImages[arrayIndex].base_path;
                const new_base_path = base_path.replace("$DEVICE", `${this.deviceName}`);
                let imagePath = baseURL + new_base_path + "/" + imageTimestamp + ".jpg";

                if (this.csrfToken) {
                    imagePath += `?csrf=${this.csrfToken}`;
                }

                const backgroundWidth = (this.imgWidth / this.srcWidth) * this.displayWidth;

                let displayHeight = 1000;

                // Create an Image object to check background image loading
                const image = new Image();

                // Add event handlers to check if the background image has loaded
                image.onerror = () => {
                    console.error("Rail inspection background image failed to load.");
                    if (this.setSomeImagesMissing) {
                        this.setSomeImagesMissing();
                    }

                    tile.classList.add("ErrorTile");
                    ReactDOM.render(
                        <MissingImageComponent
                            zoom={this.zoom}
                            backwards={this.backwardsImage}
                        />,
                        tile,
                    );
                };

                // Set the src attribute to initiate loading (there is a change this will reduce performance, didn't notice much different when testing locally)
                image.src = imagePath;

                // // Display coordinates on tiles for debugging
                // tile.innerHTML = "==========" + [coords.x, coords.y].join(', ') + "==========" + coordToCompare + "==========" + xIndex;
                //  tile.style.color = "red";
                //  tile.style.fontSize = "50px";
                // tile.style.border = "red solid 2px";
                if (
                    this.targetInspectionData &&
                    this.targetInspectionData.timestamp &&
                    this.targetInspectionData.timestamp === imageTimestamp &&
                    this.sperryMarkersVisibility
                ) {
                    const targetLineDiv = L.DomUtil.create("div");
                    targetLineDiv.setAttribute(
                        "style",
                        `position: absolute;
                        bottom: ${this.targetInspectionData.distance * 100}%;
                        background: rgba(255,0,0,0.8);
                        height: 10px;
                        width: 100%;
                    `,
                    );
                    targetLineDiv.setAttribute("class", "TargetImageLine");
                    tile.append(targetLineDiv);
                }

                if (this.sperryMarkers !== null && this.sperryMarkers.length > 0 && this.sperryMarkersVisibility) {
                    this.sperryMarkers.forEach((marker) => {
                        if (marker.timestamp === imageTimestamp) {
                            const markerDiv = L.DomUtil.create("div");

                            let heightOfTile = 1000;
                            let interpolationRatioToPixels = parseInt(heightOfTile * parseFloat(`0.${parseInt(marker.interpolationRatio * 100)}`));

                            markerDiv.setAttribute(
                                "style",
                                `position: absolute;
                                bottom: ${interpolationRatioToPixels}px;
                                background: rgba(255,0,0,0.4);
                                height: 10px;
                                width: 100%;
                                font-size: 200px;
                            `,
                            );
                            tile.append(markerDiv);
                        }
                    });
                }

                tile.setAttribute("data-timestamp", imageTimestamp);

                let styles = {};

                if (this.horizontal) {
                    if (!this.backwardsImage) {
                        if (this.flipped) {
                            styles["transform-origin"] = "top left";
                            styles["transform"] = "rotate(-90deg) translate(-100%, 0px)";
                            styles["height"] = "1000px";
                        } else {
                            styles["transform-origin"] = "0 0";
                            styles["transform"] = "translate(500px, -500px) rotate(90deg) translate(500px, -500px)";
                        }
                    } else {
                        if (this.flipped) {
                            styles["transform-origin"] = "top left";
                            styles["transform"] = "rotate(90deg) scaleX(-1) translate(-100%, -1000px)";
                            styles["height"] = "1000px";
                        } else {
                            styles["transform-origin"] = "top left";
                            styles["transform"] = "translate(500px, -500px) rotate(-90deg) scaleX(-1) translate(500px, -500px)";
                            styles["height"] = "1000px";
                        }
                    }
                } else {
                    if (this.reverse) {
                        styles["transform"] = "scaleY(-1)";
                    }
                }

                if (this.horizontalFlip) {
                    if (styles["transform"]) {
                        styles["transform"] += " scaleX(-1)";
                    } else {
                        styles["transform"] = "scaleX(-1)";
                    }
                }

                let styleString = "";
                Object.keys(styles).forEach((styleKey) => {
                    styleString += `${styleKey}: ${styles[styleKey]};`;
                });

                tile.setAttribute(
                    "style",
                    `background: url("${imagePath}") no-repeat -${this.srcX}px 0;
                    background-size: ${backgroundWidth}px 1000px; 
                    width: ${this.displayWidth}px; 
                    height: ${displayHeight}px; 
                    filter: brightness(${this.brightness}) 
                    contrast(${this.contrast});
                    position: relative;
                    ${styleString}`,
                );

                if (DEBUG) {
                    const text = document.createElement("p");
                    const { elr, mile, yard, trid } = this.railImages[arrayIndex].mwv;
                    text.textContent = `${arrayIndex} ${elr} ${mile} ${yard} ${trid}`;
                    text.style.color = "red";
                    text.style.padding = "20px";
                    text.style.fontSize = "120px";
                    tile.appendChild(text);
                }

                if (this.displayContamination) {
                    const currentContamination = _.find(this.classifications, (classification) => classification.image_timestamp === imageTimestamp);
                    const previousContamination = _.find(this.classifications, (classification) => classification.image_timestamp === previousImageTimestamp);
                    const nextContamination = _.find(this.classifications, (classification) => classification.image_timestamp === nextImageTimestamp);

                    if (currentContamination) {
                        const leftContaminated = currentContamination.bbox.left_rail.class === "contaminated";
                        const rightContaminated = currentContamination.bbox.right_rail.class === "contaminated";

                        const bottomLeftContamination = _.get(previousContamination, ["bbox", "left_rail", "class"]) === "contaminated";
                        const topLeftContamination = _.get(nextContamination, ["bbox", "left_rail", "class"]) === "contaminated";
                        const bottomRightContamination = _.get(previousContamination, ["bbox", "right_rail", "class"]) === "contaminated";
                        const topRightContamination = _.get(nextContamination, ["bbox", "right_rail", "class"]) === "contaminated";

                        if (leftContaminated) {
                            const contaminationMarkerTop = L.DomUtil.create("div");
                            const borderTop = topLeftContamination ? "" : "border-top: 12px solid yellow;";
                            const borderBottom = bottomLeftContamination ? "" : "border-bottom: 12px solid yellow;";
                            const leftOffset = 0.5;

                            contaminationMarkerTop.setAttribute(
                                "style",
                                `
                                border-left: 12px solid yellow;
                                border-right: 12px solid yellow;
                                width: ${this.contaminationWidth - leftOffset}%; 
                                height: 100%;
                                position: absolute;
                                top: 0;
                                left: ${leftOffset}%;
                                z-index: 1000;
                                ${borderTop}
                                ${borderBottom}
                                `,
                            );

                            tile.append(contaminationMarkerTop);
                        }

                        if (rightContaminated) {
                            let contaminationMarkerBottom = L.DomUtil.create("div");
                            const borderTop = topRightContamination ? "" : "border-top: 12px solid yellow;";
                            const borderBottom = bottomRightContamination ? "" : "border-bottom: 12px solid yellow;";

                            contaminationMarkerBottom.setAttribute(
                                "style",
                                `
                                border-left: 12px solid yellow;
                                border-right: 12px solid yellow;
                                width: ${this.contaminationWidth}%; 
                                height: 100%;
                                position: absolute;
                                top: 0;
                                right: 0%;
                                z-index: 1000;
                                ${borderTop}
                                ${borderBottom}
                                `,
                            );

                            tile.append(contaminationMarkerBottom);
                        }
                    }
                }

                container.append(tile);

                if (xOffset === 0) {
                    let line = L.DomUtil.create("div");
                    const lineClasses = ["line"];

                    if (this.horizontal) {
                        lineClasses.push("horizontalLine");
                    } else {
                        lineClasses.push("verticalLine");
                    }

                    if (this.isExporting && this.exportRange) {
                        const startIndex = this.exportRange.start || 0;
                        const endIndex = this.exportRange.end || this.railImages.length - 1;

                        if (arrayIndex === startIndex || arrayIndex === endIndex) {
                            lineClasses.push("exportRangeMarker");
                        }
                    }

                    line.setAttribute("class", lineClasses.join(" "));

                    container.append(line);
                }

                done(null, container);
            }, 0);
        } else {
            if (DEBUG) {
                setTimeout(() => {
                    const tile = L.DomUtil.create("div");
                    tile.style.width = "100%";
                    tile.style.height = "100%";
                    tile.style.backgroundColor = (coords.x + coords.y) % 2 === 0 ? "#8B0000" : "#A52A2A";
                    tile.style.display = "flex";
                    tile.style.alignItems = "center";
                    tile.style.justifyContent = "center";
                    tile.style.color = "white";
                    tile.style.fontWeight = "bold";
                    tile.style.fontSize = "90px";
                    container.appendChild(tile);
                    const tileWidth = tile.getBoundingClientRect().width;
                    tile.textContent = `x: ${coords.x}, y: ${coords.y}, width: ${tileWidth}px`;
                    done(null, container);
                }, 0);
            }
        }

        return container;
    },

    _getTilePos: function (coords) {
        let yOffset = this.yOffset;
        if (this.secondary) {
            yOffset += (this.manualOffset % 1) * 1000;
        }
        if (this.secondaryAlignment) {
            yOffset += this.secondaryAlignment.intra * 1000;
        }
        let xOffset = this.xOffset;
        xOffset += this.baseXOffset;
        xOffset -= Math.floor(xOffset / this.options.tileSize.x) * this.options.tileSize.x;

        let pos = L.GridLayer.prototype._getTilePos.call(this, coords);
        if (this.horizontal) {
            return pos.add([(this.reverse ? -1 : 1) * yOffset, xOffset]);
        } else {
            return pos.add([xOffset, (this.reverse ? -1 : 1) * yOffset]);
        }
    },
});

export const SecondaryInfoLayer = L.GridLayer.extend({
    railImages: null,
    arrayOffset: 0,
    zoom: 0,
    store: null,
    xOffset: 10,
    horizontal: false,
    totalWidth: 0,
    reverse: false,
    secondaryAlignment: { inter: 0, intra: 0 },
    manualOffset: 0,
    yOffset: 0,
    leftRightConfig: null,
    primaryIsReversed: false,
    setup: function (railImages, store, horizontal, totalWidth = 0, arrowLeft = true) {
        this.railImages = railImages;
        this.store = store;
        this.horizontal = horizontal;
        this.totalWidth = totalWidth;
        this.arrowLeft = arrowLeft;
    },
    setPrimaryIsReversed: function (primaryReversed) {
        this.primaryIsReversed = primaryReversed;
    },
    setLeftRightConfig: function (config) {
        this.leftRightConfig = config;
    },
    setArrayOffset: function (offset) {
        this.arrayOffset = offset;
    },
    setReverse: function (reverse) {
        this.reverse = reverse;
    },
    setMapZoom: function (zoom) {
        this.zoom = zoom;
    },
    setBaseXOffset: function (val) {
        this.baseXOffset = val;
    },
    setSecondaryAlignment: function (alignment) {
        this.secondaryAlignment = alignment;
    },
    setManualOffset: function (manualOffset) {
        this.manualOffset = manualOffset;
    },
    setConstantOffset: function (offset) {
        if (!this.horizontal || !this.flipped) {
            offset *= -1;
        }
        if (this.horizontal && this.reverse) {
            this.constantArrayOffset = Math.ceil(offset);
            this.yOffset = (this.constantArrayOffset - offset) * 1000;
        } else if (this.horizontal) {
            this.constantArrayOffset = Math.floor(offset);
            this.yOffset = (this.constantArrayOffset - offset) * 1000;
        } else {
            this.constantArrayOffset = Math.floor(offset);
            this.yOffset = (offset - this.constantArrayOffset) * 1000;
        }
    },
    createTile: function (coords, done) {
        let coordToCompare = this.horizontal ? coords.y : coords.x;
        let arrayIndexCoord = this.horizontal ? -coords.x : coords.y;

        const xIndex = Math.floor(this.baseXOffset / this.options.tileSize.x);

        let arrayIndex;

        let primaryArrayIndex = (this.primaryIsReversed ? 1 : -1) * arrayIndexCoord + this.arrayOffset;

        if (coordToCompare === xIndex) {
            arrayIndex = getSecondaryArrayIndex(
                arrayIndexCoord,
                this.secondaryAlignment,
                this.arrayOffset,
                this.primaryIsReversed,
                this.reverse,
                this.constantArrayOffset,
                this.manualOffset,
                "info layer",
            );
        }

        const container = document.createElement("div");
        if (coordToCompare === xIndex && this.railImages && arrayIndex >= 0 && arrayIndex < this.railImages.length && arrayIndex % 1 === 0) {
            const directionLetter = getDirectionLetter(this.leftRightConfig, primaryArrayIndex, coordToCompare);
            setTimeout(() => {
                ReactDOM.render(
                    <Provider store={this.store}>
                        <RailInspectTileInfo
                            key={`${this.secondary ? "secondary" : "primary"}-${arrayIndex}`}
                            leftArrow={this.arrowLeft}
                            rightArrow={coordToCompare === 0}
                            railImages={this.railImages}
                            arrayIndex={arrayIndex}
                            zoom={this.zoom}
                            horizontal={this.horizontal}
                            reverse={this.reverse}
                            directionLetter={directionLetter}
                            secondary
                        />
                    </Provider>,
                    container,
                );
                done(null, container);
            }, 0);
        } else {
            done(null, container);
        }

        return container;
    },
    destroyTile: function (tile) {
        if (tile) {
            ReactDOM.unmountComponentAtNode(tile);
        }
    },
    _getTilePos: function (coords) {
        let yOffset = this.yOffset;

        yOffset += (this.manualOffset % 1) * 1000;

        if (this.secondaryAlignment) {
            yOffset += this.secondaryAlignment.intra * 1000;
        }

        let xOffset = this.xOffset;
        xOffset += this.baseXOffset;
        xOffset -= Math.floor(xOffset / this.options.tileSize.x) * this.options.tileSize.x;

        let pos = L.GridLayer.prototype._getTilePos.call(this, coords);
        if (this.horizontal) {
            return pos.add([(this.reverse ? -1 : 1) * yOffset, xOffset]);
        } else {
            return pos.add([xOffset, (this.reverse ? -1 : 1) * yOffset]);
        }
    },
});

export const InfoLayer = L.GridLayer.extend({
    railImages: null,
    arrayOffset: 0,
    zoom: 0,
    store: null,
    reverse: false,
    xOffset: 0,
    horizontal: false,
    arrowLeft: true,
    inspectionOffset: 0,
    setup: function (railImages, store, horizontal, totalWidth = 0, arrowLeft = true, inspectionOffset) {
        this.railImages = railImages;
        this.store = store;
        this.horizontal = horizontal;
        this.totalWidth = totalWidth;
        this.arrowLeft = arrowLeft;
        this.inspectionOffset = inspectionOffset;
        this._renderedTiles = new Set();
    },
    setArrayOffset: function (offset) {
        this.arrayOffset = offset;
    },
    setReverse: function (reverse) {
        this.reverse = reverse;
    },
    setMapZoom: function (zoom) {
        this.zoom = zoom;
    },
    setXOffset: function (val) {
        this.xOffset = val;
    },
    createTile: function (coords, done) {
        let coordToCompare = this.horizontal ? coords.y : coords.x;
        let arrayIndexCoord = this.horizontal ? -coords.x : coords.y;

        let arrayIndex = (this.reverse ? 1 : -1) * arrayIndexCoord + this.arrayOffset;

        const container = document.createElement("div");
        this._renderedTiles.add(container);

        if (
            (coordToCompare === -1 || (coordToCompare === 0 && !this.arrowLeft && !this.horizontal)) &&
            this.railImages &&
            arrayIndex >= 0 &&
            arrayIndex < this.railImages.length &&
            arrayIndex % 1 === 0
        ) {
            requestAnimationFrame(() => {
                if (!container.isConnected) {
                    done(null, container);
                    return;
                }

                ReactDOM.render(
                    <Provider store={this.store}>
                        <RailInspectTileInfo
                            leftArrow={this.arrowLeft}
                            rightArrow={coordToCompare === 0}
                            railImages={this.railImages}
                            arrayIndex={arrayIndex}
                            zoom={this.zoom}
                            horizontal={this.horizontal}
                            reverse={this.reverse}
                            sessionOffset={this.inspectionOffset}
                        />
                    </Provider>,
                    container,
                );
                done(null, container);
            });
        } else {
            done(null, container);
        }

        return container;
    },
    destroyTile: function (tile) {
        if (tile) {
            ReactDOM.unmountComponentAtNode(tile);
        }
    },
    onRemove: function (map) {
        if (this._renderedTiles) {
            for (const tile of this._renderedTiles) {
                ReactDOM.unmountComponentAtNode(tile);
            }
            this._renderedTiles.clear();
        }
        L.GridLayer.prototype.onRemove.call(this, map);
    },
    _getTilePos: function (coords) {
        let pos = L.GridLayer.prototype._getTilePos.call(this, coords);
        pos = pos.add([this.xOffset, 0]);
        if (coords.x === 0 && !this.horizontal) {
            pos = pos.add([this.totalWidth - this._tileSize.x, 0]);
        }
        return pos;
    },
});

export const LeftRightDisplayLayer = L.GridLayer.extend({
    railImages: null,
    arrayOffset: 0,
    zoom: 0,
    reverse: false,
    xOffset: 0,
    leftRightConfig: {},
    horizontal: false,
    railImagesWidth: 0,
    constantArrayOffset: 0,
    yOffset: 0,
    secondaryHasRendered: false,
    setup: function (railImages, leftRightConfig, horizontal, railImagesWidth) {
        this.railImages = railImages;
        this.leftRightConfig = leftRightConfig;
        this.horizontal = horizontal;
        this.railImagesWidth = railImagesWidth;
    },
    setArrayOffset: function (offset) {
        this.arrayOffset = offset;
    },
    setReverse: function (reverse) {
        this.reverse = reverse;
    },
    setMapZoom: function (zoom) {
        this.zoom = zoom;
    },
    setXOffset: function (val) {
        this.xOffset = val;
    },
    setSecondaryHasRendered: function (val) {
        this.secondaryHasRendered = val;
    },
    setConstantOffset: function (offset) {
        offset *= -1;
        this.constantArrayOffset = Math.floor(offset);
        this.yOffset = (offset - this.constantArrayOffset) * 1000;
    },
    createTile: function (coords) {
        let coordToCompare = this.horizontal ? coords.y : coords.x;
        let arrayIndexCoord = this.horizontal ? -coords.x : coords.y;

        let arrayIndex = (this.reverse ? 1 : -1) * arrayIndexCoord + this.arrayOffset;

        let container = L.DomUtil.create("div");

        let passedCoordCriteria = false;

        if (this.horizontal) {
            if (coordToCompare === -1 || (coordToCompare === 1 && this.secondaryHasRendered === false)) {
                passedCoordCriteria = true;
            }
        } else {
            if (coordToCompare === -1 || (coordToCompare === 0 && this.secondaryHasRendered === false)) {
                passedCoordCriteria = true;
            }
        }

        if (passedCoordCriteria && this.railImages && arrayIndex >= 0 && arrayIndex < this.railImages.length) {
            let tile = L.DomUtil.create("div");

            const top = coordToCompare === -1;

            const containerClassNames = ["LeftRightContainer", top ? "Top" : "Bottom"];
            if (this.horizontal) {
                containerClassNames.push("Horizontal");
            } else {
                containerClassNames.push("Vertical");
            }
            tile.setAttribute("class", containerClassNames.join(" "));

            const pElem = L.DomUtil.create("p");
            const pElemClassNames = ["Text", this.reverse ? "Reverse" : ""];
            pElem.setAttribute("class", pElemClassNames.join(" "));
            pElem.innerHTML = getDirectionLetter(this.leftRightConfig, arrayIndex, coordToCompare, false);

            tile.append(pElem);
            container.append(tile);
        }

        return container;
    },
    destroyTile: function (tile) {
        if (tile) {
            ReactDOM.unmountComponentAtNode(tile);
        }
    },
    _getTilePos: function (coords) {
        let pos = L.GridLayer.prototype._getTilePos.call(this, coords);
        if (this.horizontal) {
            return pos.add([this.xOffset, 0]);
        } else {
            pos = pos.add([0, this.yOffset]);
            if (coords.x === 0) {
                return pos.add([this.railImagesWidth, 0]);
            } else {
                return pos;
            }
        }
    },
});

export const GridOverlay = L.GridLayer.extend({
    zoom: 0,
    gridSize: 10,
    offset: { x: 0, y: 0 },
    setup: function (railImages) {
        this.railImages = railImages;
    },
    setMapZoom: function (zoom) {
        this.zoom = zoom;
    },
    setGridSize: function (gridSize) {
        this.gridSize = gridSize;
    },
    setGridOffset: function (offset) {
        this.offset = offset;
    },
    createTile: function (coords) {
        let arrayIndexCoord = coords.x;
        let arrayIndex = arrayIndexCoord;
        let container = document.createElement("div");

        if (this.railImages && arrayIndex < this.railImages.length && arrayIndex % 1 === 0) {
            container.classList.add("GridLayerTile");
            container.classList.add("width-" + this.gridSize);
        }

        return container;
    },
    _getTilePos: function (coords) {
        let pos = L.GridLayer.prototype._getTilePos.call(this, coords);
        return pos.add([this.offset.x, this.offset.y]);
    },
});

export const DetectionsLayer = L.GridLayer.extend({
    railImages: null,
    arrayOffset: 0,
    zoom: 0,
    reverse: false,
    xOffset: 0,
    horizontal: false,
    detections: {},
    constantArrayOffset: 0,
    closestDetection: null,
    backwardsImage: false,
    flipped: false,
    imagesConfig: [],
    clickHandler: () => {},
    setup: function (railImages, detections, horizontal) {
        this.railImages = railImages;
        this.detections = detections;
        this.horizontal = horizontal;
    },
    setClickHandler: function (func) {
        this.clickHandler = func;
    },
    setClosestDetection: function (detection) {
        this.closestDetection = detection;
    },
    setArrayOffset: function (offset) {
        this.arrayOffset = offset;
    },
    setReverse: function (reverse) {
        this.reverse = reverse;
    },
    setMapZoom: function (zoom) {
        this.zoom = zoom;
    },
    setXOffset: function (val) {
        this.xOffset = val;
    },
    setFlipped: function (flipped) {
        this.flipped = flipped;
    },
    setBackwardImage: function (back) {
        this.backwardsImage = back;
    },
    setHorizontalFlip: function (flip) {
        this.horizontalFlip = flip;
    },
    setImagesConfig: function (imagesConfig) {
        this.imagesConfig = imagesConfig;
    },
    setConstantOffset: function (offset) {
        offset *= -1;
        this.constantArrayOffset = Math.floor(offset);
        this.yOffset = (offset - this.constantArrayOffset) * 1000;
    },
    createTile: function (coords, done) {
        let coordToCompare = this.horizontal ? coords.y : coords.x;
        let arrayIndexCoord = this.horizontal ? -coords.x : coords.y;

        let arrayIndex = (this.reverse ? 1 : -1) * arrayIndexCoord + this.arrayOffset + this.constantArrayOffset;

        let container = document.createElement("div");
        let innerContainer = document.createElement("div");
        container.append(innerContainer);

        if (this.horizontal) {
            if (!this.backwardsImage) {
                if (this.flipped) {
                    innerContainer.classList.add("horizontalReversed");
                } else {
                    innerContainer.classList.add("horizontal");
                }
            } else {
                if (this.flipped) {
                    innerContainer.classList.add("backwardsImageFlipped");
                } else {
                    innerContainer.classList.add("backwardsImage");
                }
            }
        }

        if (coordToCompare === 0 && this.railImages && arrayIndex >= 0 && arrayIndex < this.railImages.length) {
            const imageTimestamp = this.railImages[arrayIndex].timestamp;
            const currentObservations = this.detections[imageTimestamp];

            let imagesConfig = this.imagesConfig;

            setTimeout(() => {
                if (currentObservations && currentObservations.length) {
                    currentObservations.forEach((observation) => {
                        observation.detections
                            .filter((detection) => {
                                const filter = detection.render_bbox && detection.image_timestamp === imageTimestamp;
                                return filter;
                            })
                            .forEach((detection) => {
                                let done = false;
                                let yOffset = 0;
                                let xOffset = 0;
                                let bbLeft = detection.bbox[0];
                                let bbTop = detection.bbox[1];

                                let width = detection.bbox[2];
                                const height = detection.bbox[3];

                                let verticalStyleName = "top";

                                if (this.horizontal) {
                                    _.forEach(imagesConfig, (image, index) => {
                                        const imgSource = _.get(image, "source", null);

                                        if (imgSource === detection.source_device) {
                                            if (bbLeft >= image.src_x && bbLeft < image.src_width + image.src_x) {
                                                bbLeft -= image.src_x;
                                                bbLeft = (bbLeft / image.src_width) * image.display_width;
                                                width = (width / image.src_width) * image.display_width;
                                                if (this.flipped) {
                                                    bbLeft += 1000 - image.display_width;
                                                }
                                                done = true;

                                                // move detection x left or right
                                                if (done) {
                                                    if (this.backwardsImage) {
                                                        xOffset = (image.offset_y_backward || 0) * 1000;
                                                    } else {
                                                        xOffset = (image.offset_y_forward || 0) * 1000;
                                                    }
                                                    bbTop -= xOffset;
                                                }
                                                return false;
                                            }
                                        }
                                        yOffset += image.display_width;

                                        if (Object.keys(imagesConfig).length - 1 === index && !done) {
                                            done = true;
                                        }
                                    });
                                } else {
                                    _.forEach(imagesConfig, (image, index) => {
                                        const imgSource = _.get(image, "source", null);

                                        if (imgSource === detection.source_device) {
                                            const transformedImageWidth = (image.img_width / image.src_width) * image.display_width;
                                            const squashRatio = transformedImageWidth / image.img_width;
                                            bbLeft *= squashRatio;
                                            width *= squashRatio;
                                        }
                                    });

                                    if (this.reverse) {
                                        verticalStyleName = "bottom";
                                    }
                                    done = true;
                                }

                                if (this.horizontal) {
                                    if (this.flipped) {
                                        bbLeft -= yOffset;
                                    } else {
                                        bbLeft += yOffset;
                                    }
                                }

                                if (done) {
                                    const bb = L.DomUtil.create("div");

                                    // const markerText = L.DomUtil.create("div");
                                    // markerText.textContent = `${detection.id} - ${JSON.stringify(detection.bbox)}`;
                                    // markerText.setAttribute(
                                    //     "style",
                                    //     `position: absolute;
                                    //         background: rgba(255,0,0,0.4);
                                    //         padding-left: 10px;
                                    //         padding-right: 10px;
                                    //         font-size: 50px;
                                    //         color: white;
                                    //     `,
                                    // );
                                    //
                                    // bb.append(markerText);

                                    bb.addEventListener("click", () => {
                                        // if (_.get(detection, "id") !== _.get(this.closestDetection, "id")) {
                                        this.clickHandler(_.get(observation, "id"));
                                        // }
                                    });

                                    bb.setAttribute("class", "BoundingBox");
                                    if (this.closestDetection) {
                                        if (observation.id === this.closestDetection.id) {
                                            bb.setAttribute("class", "BoundingBox closest");
                                        }
                                    }
                                    bb.setAttribute(
                                        "style",
                                        `position:absolute; ${verticalStyleName}:${bbTop}px; left:${bbLeft}px; width:${width}px; height:${height}px`,
                                    );
                                    innerContainer.append(bb);
                                }
                            });
                    });
                }
                done(null, container);
            }, 0);
        } else {
            done(null, container);
        }
        return container;
    },
    destroyTile: function (tile) {
        if (tile) {
            ReactDOM.unmountComponentAtNode(tile);
        }
    },
    _getTilePos: function (coords) {
        let pos = L.GridLayer.prototype._getTilePos.call(this, coords);
        return pos.add([this.xOffset, 0]);
    },
});

export const RightInfoLayer = L.GridLayer.extend({
    railImages: null,
    arrayOffset: 0,
    zoom: 0,
    reverse: false,
    xOffset: 0,
    yOffset: 0,
    areasOfInterest: [],
    setup: function (railImages, areasOfInterest) {
        this.railImages = railImages;
        this.areasOfInterest = areasOfInterest;
    },
    setArrayOffset: function (offset) {
        this.arrayOffset = offset;
    },
    setReverse: function (reverse) {
        this.reverse = reverse;
    },
    setMapZoom: function (zoom) {
        this.zoom = zoom;
    },
    setXOffset: function (val) {
        this.xOffset = val;
    },
    createTile: function (coords) {
        let container = L.DomUtil.create("div");

        let coordToCompare = coords.x;
        let arrayIndexCoord = coords.y;

        let arrayIndex = (this.reverse ? 1 : -1) * arrayIndexCoord + this.arrayOffset;

        if (coordToCompare === 1 && this.railImages && arrayIndex >= 0 && arrayIndex < this.railImages.length && arrayIndex % 1 === 0) {
            let areaMatch;
            const imageTimestamp = this.railImages[arrayIndex].timestamp;

            let beforeTimestamp;
            let afterTimestamp;
            let isStart = false;
            let isEnd = false;

            if (this.railImages.length > arrayIndex + 1) {
                afterTimestamp = this.railImages[arrayIndex + 1].timestamp;
            }

            if (arrayIndex - 1 > -1) {
                beforeTimestamp = this.railImages[arrayIndex - 1].timestamp;
            }

            this.areasOfInterest.forEach((area) => {
                if (imageTimestamp / 1000 > area.start && imageTimestamp / 1000 < area.end) {
                    areaMatch = area;

                    if (beforeTimestamp / 1000 < area.start) {
                        isStart = true;
                    }

                    if (afterTimestamp / 1000 > area.end) {
                        isEnd = true;
                    }
                }
            });

            if (areaMatch) {
                let tile = L.DomUtil.create("div");
                const containerClassNames = ["AreaOfInterestContainer"];
                let text;

                if (isStart) {
                    containerClassNames.push(this.reverse ? "End" : "Start");
                    text = `${areaMatch.type} - Start`;
                } else if (isEnd) {
                    containerClassNames.push(this.reverse ? "Start" : "End");
                    text = `${areaMatch.type} - End`;
                }
                tile.setAttribute("class", containerClassNames.join(" "));

                if (text) {
                    const pElem = L.DomUtil.create("p");
                    const pElemClassNames = ["Text", this.reverse ? "Reverse" : ""];
                    pElem.setAttribute("class", pElemClassNames.join(" "));
                    pElem.innerHTML = text;
                    tile.append(pElem);
                }

                container.append(tile);
            }
        }

        return container;
    },
    destroyTile: function (tile) {
        if (tile) {
            ReactDOM.unmountComponentAtNode(tile);
        }
    },
    _getTilePos: function (coords) {
        let pos = L.GridLayer.prototype._getTilePos.call(this, coords);
        return pos.add([this.xOffset, 0]);
    },
});
